import { ERROR_CODES, MESSAGE_TYPES, TIMING_CONSTANTS } from './constants.js'
import { packInterWorkerMessage, unpackInterWorkerMessageData } from './inter_worker_message.js'
import type { InterWorkerMessage, WorkerRequest } from './types.js'
import { bytesToHex, getTimeRangeFromNow } from './utils.js'
import { packWorkerRequest } from './worker_request.js'

const randomUUID = (): string => globalThis.crypto.randomUUID()

export const API_ENDPOINTS = {
  SUBMIT_MESSAGE: '/api/v1/worker/submit_inter_worker_message',
  CONSUME_MESSAGE: '/api/v1/worker/consume_inter_worker_message',
  CALL: '/api/v1/worker/call',
} as const

export interface PostWorkerRequestArgs {
  publicKey: Uint8Array
  privKey: Uint8Array
  serviceUrl: string
  path: string
  payload: unknown
}

export const postWorkerRequest = async ({
  publicKey,
  privKey,
  serviceUrl,
  path,
  payload,
}: PostWorkerRequestArgs): Promise<unknown> => {
  const signedRequest = packWorkerRequest({ publicKey, privKey, payload })

  const { code, message, data } = await post(serviceUrl, path, signedRequest)

  if (code !== ERROR_CODES.SUCCESS) {
    throw new Error(`[${code}] ${message}, path=${path}`)
  }

  return data
}

export const submitInterWorkerMessage = (args: {
  publicKey: Uint8Array
  privKey: Uint8Array
  serviceUrl: string
  payload: InterWorkerMessage | (InterWorkerMessage & { request_id: string })
}): Promise<unknown> => {
  return postWorkerRequest({ ...args, path: API_ENDPOINTS.SUBMIT_MESSAGE })
}

export const consumeInterWorkerMessage = (args: {
  publicKey: Uint8Array
  privKey: Uint8Array
  serviceUrl: string
  payload: { message_type: string }
}): Promise<{ requests?: WorkerRequest[] }> => {
  return postWorkerRequest({ ...args, path: API_ENDPOINTS.CONSUME_MESSAGE }) as Promise<{
    requests?: WorkerRequest[]
  }>
}

interface PostResponse {
  code: number
  message: string
  data: any
}

const post = async (serviceUrl: string, path: string, data: unknown): Promise<PostResponse> => {
  const res = await fetch(serviceUrl + path, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

  return res.json() as Promise<PostResponse>
}

export interface CallViaHTTPArgs {
  publicKey: Uint8Array
  privKey: Uint8Array
  serviceUrl: string
  receiver: string
  method: string
  params?: unknown[]
  headers?: Record<string, unknown>
  timeout?: number
}

export const callViaHTTP = async ({
  publicKey,
  privKey,
  serviceUrl,
  receiver,
  method,
  params = [],
  headers = {},
  timeout = TIMING_CONSTANTS.DEFAULT_HTTP_CALL_TIMEOUT,
}: CallViaHTTPArgs): Promise<unknown> => {
  const request_id = randomUUID()

  const requestData = {
    type: MESSAGE_TYPES.REQUEST,
    request_id,
    method,
    params,
    headers,
  } as const

  const encryptedMessage = await packInterWorkerMessage(privKey, {
    publicKey,
    receiver_public_key: receiver,
    expire_at: getTimeRangeFromNow(timeout / 1000)[1].toISOString(),
    data: requestData as any,
  })

  const payload = {
    sender_public_key: bytesToHex(publicKey),
    receiver_public_key: receiver,
    data: encryptedMessage.data,
    salt: encryptedMessage.salt,
    expire_at: encryptedMessage.expire_at,
    timeout,
    request_id,
  }

  const workerRequest = packWorkerRequest({ publicKey, privKey, payload })

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout + 5000)

  try {
    const res = await fetch(serviceUrl + API_ENDPOINTS.CALL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(workerRequest),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    const result: any = await res.json()

    if (!result.success) {
      const errorData = result.error || {}
      const err = new Error(errorData.message || 'RPC call failed') as Error & {
        code?: number
        request_id?: string
        retryable?: boolean
      }
      err.code = errorData.code || ERROR_CODES.INTERNAL_ERROR
      err.request_id = errorData.request_id || request_id

      if (err.code === 503) {
        err.retryable = true
        err.message = 'Target worker is offline, please retry later'
      }

      throw err
    }

    const responseData = await unpackInterWorkerMessageData(privKey, {
      sender_public_key: result.data.sender_public_key || receiver,
      data: result.data.data,
      salt: result.data.salt,
    })

    if (responseData.type !== MESSAGE_TYPES.RESPONSE) {
      throw new Error(`Invalid response type: ${responseData.type}`)
    }

    if (responseData.request_id !== request_id) {
      throw new Error(`Request ID mismatch: expected ${request_id}, got ${responseData.request_id}`)
    }

    if (responseData.code !== ERROR_CODES.SUCCESS) {
      const err = new Error(responseData.message || 'RPC execution error') as Error & {
        code?: number
      }
      err.code = responseData.code || ERROR_CODES.INTERNAL_ERROR
      throw err
    }

    return responseData.data
  } catch (err: any) {
    clearTimeout(timeoutId)

    if (err.name === 'AbortError') {
      const timeoutErr = new Error(`RPC call timeout after ${timeout}ms`) as Error & {
        code?: number
      }
      timeoutErr.code = ERROR_CODES.TIMEOUT
      throw timeoutErr
    }

    throw err
  }
}
